/**
 * FreeTierAuditCard — iter87.91 (iter87.102 — add search + actions + bulk)
 *
 * Free-tier league requests. Auto-approve via Turnstile is the happy path
 * (iter87.80), but when the widget misfires or a bot slips through, rows
 * park in `pending` and the operator needs a way to clear the backlog.
 * This card now surfaces per-row and bulk approve/reject/delete-forever
 * actions plus a search box to find specific rows fast.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RefreshCw, Check, Clock, X, Search, Trash2 } from "lucide-react";

const PAGE_SIZE = 50;

function StatusPill({ status, autoApproved }) {
    const base = "inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded";
    if (status === "approved") {
        return (
            <span
                className={`${base} border border-green-500/40 text-green-400`}
                data-testid={`free-audit-status-${status}`}
            >
                <Check className="w-3 h-3" />
                {autoApproved ? "Auto-approved" : "Approved"}
            </span>
        );
    }
    if (status === "rejected") {
        return (
            <span
                className={`${base} border border-red-500/40 text-red-400`}
                data-testid={`free-audit-status-${status}`}
            >
                <X className="w-3 h-3" />
                Rejected
            </span>
        );
    }
    return (
        <span
            className={`${base} border border-cyber/40 text-cyber`}
            data-testid={`free-audit-status-${status}`}
        >
            <Clock className="w-3 h-3" />
            Pending
        </span>
    );
}

function _fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
    } catch {
        return iso;
    }
}

export default function FreeTierAuditCard() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selected, setSelected] = useState(() => new Set());
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const arr = await api.siteListLeagueRequests(null, true, "free");
            arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
            setRows(arr);
            setSelected(new Set());
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load free-tier history");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const statusCounts = useMemo(() => {
        const c = { all: rows.length, pending: 0, approved: 0, rejected: 0 };
        rows.forEach((r) => { if (c[r.status] !== undefined) c[r.status] += 1; });
        return c;
    }, [rows]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => {
            if (statusFilter !== "all" && r.status !== statusFilter) return false;
            if (!q) return true;
            return (
                (r.league_name || "").toLowerCase().includes(q) ||
                (r.admin_email || "").toLowerCase().includes(q) ||
                (r.admin_username || "").toLowerCase().includes(q) ||
                (r.status || "").toLowerCase().includes(q)
            );
        });
    }, [rows, search, statusFilter]);

    const visible = showAll ? filtered : filtered.slice(0, PAGE_SIZE);
    const autoApprovedCount = rows.filter(
        (r) => r.status === "approved" && r.approval_source === "turnstile_auto_approve"
    ).length;

    const visibleIds = useMemo(() => visible.map((r) => r.id), [visible]);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    const anySelected = selected.size > 0;
    const selectedRows = useMemo(
        () => rows.filter((r) => selected.has(r.id)),
        [rows, selected]
    );
    const selectedPendingIds = selectedRows.filter((r) => r.status === "pending").map((r) => r.id);
    // Backend `_deletable` guard: rejected OR archived. Pending (unless
    // archived) and approved rows are protected. Filter the selection
    // client-side so bulk-delete doesn't silently skip.
    const selectedDeletableIds = selectedRows
        .filter((r) => r.status === "rejected" || !!r.archived_at)
        .map((r) => r.id);

    const toggleRow = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleAllVisible = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                visibleIds.forEach((id) => next.delete(id));
            } else {
                visibleIds.forEach((id) => next.add(id));
            }
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    const approveOne = async (id) => {
        if (busy) return;
        setBusy(true);
        try {
            await api.siteApproveLeagueRequest(id);
            toast.success("Approved");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Approve failed");
        }
        setBusy(false);
    };

    const rejectOne = async (id) => {
        const reason = window.prompt("Reject reason (optional):", "");
        if (reason === null) return;
        if (busy) return;
        setBusy(true);
        try {
            await api.siteRejectLeagueRequest(id, reason || null);
            toast.success("Rejected");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reject failed");
        }
        setBusy(false);
    };

    const deleteOne = async (id, label) => {
        if (!window.confirm(`Delete forever: ${label}?\n\nThis cannot be undone.`)) return;
        if (busy) return;
        setBusy(true);
        try {
            await api.siteBulkDeleteLeagueRequestsForever([id]);
            toast.success("Deleted forever");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
        setBusy(false);
    };

    const bulkApprove = async () => {
        if (selectedPendingIds.length === 0) {
            toast.error("No pending rows selected");
            return;
        }
        if (!window.confirm(`Approve ${selectedPendingIds.length} pending request(s)?`)) return;
        if (busy) return;
        setBusy(true);
        try {
            const res = await api.siteBulkApproveLeagueRequests(selectedPendingIds);
            toast.success(res?.message || `Approved ${selectedPendingIds.length}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk approve failed");
        }
        setBusy(false);
    };

    const bulkReject = async () => {
        if (selectedPendingIds.length === 0) {
            toast.error("No pending rows selected");
            return;
        }
        const reason = window.prompt(
            `Reject ${selectedPendingIds.length} pending request(s). Reason (optional):`,
            ""
        );
        if (reason === null) return;
        if (busy) return;
        setBusy(true);
        try {
            const res = await api.siteBulkRejectLeagueRequests(selectedPendingIds, reason || null);
            toast.success(res?.message || `Rejected ${selectedPendingIds.length}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk reject failed");
        }
        setBusy(false);
    };

    const bulkDelete = async () => {
        if (selectedDeletableIds.length === 0) {
            toast.error("No deletable rows selected (delete-forever needs rejected or archived rows)");
            return;
        }
        if (!window.confirm(
            `Delete forever ${selectedDeletableIds.length} row(s)?\n\nThis removes them from the audit trail permanently and cannot be undone. Approved rows and non-archived pending rows are skipped.`
        )) return;
        if (busy) return;
        setBusy(true);
        try {
            const res = await api.siteBulkDeleteLeagueRequestsForever(selectedDeletableIds);
            toast.success(res?.message || `Deleted ${res?.deleted_count ?? selectedDeletableIds.length}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk delete failed");
        }
        setBusy(false);
    };

    return (
        <div className="space-y-3" data-testid="free-tier-audit-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[11px] text-zinc-400">
                    <span className="text-white font-bold">{rows.length}</span> total ·{" "}
                    <span className="text-green-400 font-bold">{autoApprovedCount}</span> auto-approved via Turnstile
                    {search.trim() && (
                        <span className="text-zinc-500">
                            {" "}· <span className="text-white font-bold">{filtered.length}</span> match "{search.trim()}"
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="brutal-btn-ghost text-xs py-1.5"
                    data-testid="free-tier-audit-refresh"
                    title="Refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            {/* Status filter pills */}
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="free-tier-audit-status-pills">
                {[
                    { key: "all", label: "All" },
                    { key: "pending", label: "Pending" },
                    { key: "approved", label: "Approved" },
                    { key: "rejected", label: "Rejected" },
                ].map((p) => {
                    const active = statusFilter === p.key;
                    return (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => setStatusFilter(p.key)}
                            data-testid={`free-tier-audit-pill-${p.key}`}
                            className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide rounded-full border transition-colors ${
                                active
                                    ? "border-cyber bg-cyber text-black"
                                    : "border-[#333] text-zinc-400 hover:border-cyber/50 hover:text-white"
                            }`}
                        >
                            {p.label} <span className={active ? "text-black/70" : "text-zinc-600"}>{statusCounts[p.key] || 0}</span>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by Rivalry, admin, email, or status…"
                    className="w-full pl-9 pr-3 py-2 text-xs bg-[#0d0d0d] border border-[#222] rounded focus:border-cyber focus:outline-none text-white"
                    data-testid="free-tier-audit-search"
                />
            </div>

            {/* Bulk action bar — visible when any row is selected */}
            {anySelected && (
                <div
                    className="flex items-center justify-between gap-2 rounded border border-cyber/40 bg-cyber/5 px-3 py-2 text-[11px]"
                    data-testid="free-tier-audit-bulk-bar"
                >
                    <div className="text-white">
                        <span className="font-bold">{selected.size}</span> selected
                        {selectedPendingIds.length > 0 && (
                            <span className="text-zinc-400"> · {selectedPendingIds.length} pending</span>
                        )}
                        {selectedDeletableIds.length > 0 && (
                            <span className="text-zinc-400"> · {selectedDeletableIds.length} deletable</span>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={bulkApprove}
                            disabled={busy || selectedPendingIds.length === 0}
                            className="brutal-btn text-[11px] py-1 px-2 border-green-500/60 text-green-400 disabled:opacity-40"
                            data-testid="free-tier-audit-bulk-approve"
                            title={selectedPendingIds.length === 0 ? "Select at least one pending row" : "Approve pending"}
                        >
                            <Check className="w-3 h-3" /> Approve
                        </button>
                        <button
                            type="button"
                            onClick={bulkReject}
                            disabled={busy || selectedPendingIds.length === 0}
                            className="brutal-btn text-[11px] py-1 px-2 border-red-500/60 text-red-400 disabled:opacity-40"
                            data-testid="free-tier-audit-bulk-reject"
                            title={selectedPendingIds.length === 0 ? "Select at least one pending row" : "Reject pending"}
                        >
                            <X className="w-3 h-3" /> Reject
                        </button>
                        <button
                            type="button"
                            onClick={bulkDelete}
                            disabled={busy || selectedDeletableIds.length === 0}
                            className="brutal-btn text-[11px] py-1 px-2 border-red-500/60 text-red-400 disabled:opacity-40"
                            data-testid="free-tier-audit-bulk-delete"
                            title={selectedDeletableIds.length === 0 ? "Only rejected or archived rows can be deleted forever" : "Delete forever"}
                        >
                            <Trash2 className="w-3 h-3" /> Delete forever
                        </button>
                        <button
                            type="button"
                            onClick={clearSelection}
                            className="text-zinc-500 hover:text-white text-[11px] px-1"
                            data-testid="free-tier-audit-clear-selection"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}

            {loading && rows.length === 0 ? (
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center">
                    Loading…
                </div>
            ) : rows.length === 0 ? (
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center" data-testid="free-tier-audit-empty">
                    No free-tier requests recorded yet.
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-xs text-zinc-500 p-4 border-2 border-dashed border-zinc-800 text-center" data-testid="free-tier-audit-no-match">
                    No rows match "{search.trim()}".
                </div>
            ) : (
                <>
                    <div className="rounded border border-[#222] overflow-x-auto">
                        <table className="w-full min-w-[720px] text-xs" data-testid="free-tier-audit-table">
                            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 bg-[#0d0d0d]">
                                <tr>
                                    <th className="w-8 px-3 py-2">
                                        <input
                                            type="checkbox"
                                            checked={allVisibleSelected}
                                            onChange={toggleAllVisible}
                                            className="accent-cyber cursor-pointer"
                                            data-testid="free-tier-audit-select-all"
                                            aria-label="Select all visible rows"
                                        />
                                    </th>
                                    <th className="text-left px-3 py-2">Rivalry</th>
                                    <th className="text-left px-3 py-2">Admin</th>
                                    <th className="text-left px-3 py-2">Status</th>
                                    <th className="text-left px-3 py-2">Submitted</th>
                                    <th className="text-right px-3 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((r) => {
                                    const isPending = r.status === "pending";
                                    const isDeletable = r.status === "rejected" || !!r.archived_at;
                                    const isChecked = selected.has(r.id);
                                    return (
                                        <tr
                                            key={r.id}
                                            className={`border-t border-[#222] ${isChecked ? "bg-cyber/5" : "hover:bg-[#111]/60"}`}
                                            data-testid={`free-tier-audit-row-${r.id}`}
                                        >
                                            <td className="px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => toggleRow(r.id)}
                                                    className="accent-cyber cursor-pointer"
                                                    data-testid={`free-tier-audit-select-${r.id}`}
                                                    aria-label={`Select ${r.league_name || r.id}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-white">
                                                {r.league_name || "—"}
                                                {r.archived_at && (
                                                    <span className="ml-2 text-[9px] uppercase text-zinc-600">Archived</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-zinc-400">
                                                <div className="text-white">{r.admin_username || "—"}</div>
                                                <div className="text-[10px] text-zinc-500">{r.admin_email || "—"}</div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <StatusPill
                                                    status={r.status}
                                                    autoApproved={r.approval_source === "turnstile_auto_approve"}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">
                                                {_fmtDate(r.created_at)}
                                            </td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                <div className="inline-flex gap-1">
                                                    {isPending && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => approveOne(r.id)}
                                                                disabled={busy}
                                                                className="brutal-btn text-[10px] py-1 px-2 border-green-500/60 text-green-400 disabled:opacity-40"
                                                                data-testid={`free-tier-audit-approve-${r.id}`}
                                                                title="Approve"
                                                            >
                                                                <Check className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => rejectOne(r.id)}
                                                                disabled={busy}
                                                                className="brutal-btn text-[10px] py-1 px-2 border-red-500/60 text-red-400 disabled:opacity-40"
                                                                data-testid={`free-tier-audit-reject-${r.id}`}
                                                                title="Reject"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteOne(r.id, r.league_name || r.admin_email || r.id)}
                                                        disabled={busy || !isDeletable}
                                                        className="brutal-btn-ghost text-[10px] py-1 px-2 text-zinc-500 hover:text-red-400 disabled:opacity-30 disabled:hover:text-zinc-500 disabled:cursor-not-allowed"
                                                        data-testid={`free-tier-audit-delete-${r.id}`}
                                                        title={isDeletable ? "Delete forever" : "Reject or archive first"}
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {filtered.length > PAGE_SIZE && (
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowAll((v) => !v)}
                                className="brutal-btn-ghost text-xs py-1.5"
                                data-testid="free-tier-audit-toggle-all"
                            >
                                {showAll
                                    ? `Show latest ${PAGE_SIZE}`
                                    : `Show all ${filtered.length} rows`}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
